<script setup lang="ts">
// 外部AIエージェント（MCP）の接続トークン管理（#548 A2-5）。本人の発行済みトークンを
// 一覧し、失効できる。叩くのは user MCP サーバ（useUserMcpApi）の GET /tokens / POST /tokens/{jti}/revoke。
import { mdiDelete, mdiRobotOutline } from '@mdi/js'
import { useUserMcpApi } from '~/composables/api/useUserMcpApi'

const { listTokens, revokeToken, base } = useUserMcpApi()

interface Row {
  jti: string
  scope: string
  createdAt: number
  expiresAt: number
  revoked: boolean
}

const configured = computed(() => !!base)
const loading = ref(true)
const error = ref(false)
const tokens = ref<Row[]>([])
const confirmJti = ref<string | null>(null)
const revoking = ref(false)

const reload = async (): Promise<void> => {
  if (!configured.value) {
    loading.value = false
    return
  }
  loading.value = true
  error.value = false
  try {
    const res = await listTokens()
    tokens.value = res.tokens
  }
  catch {
    error.value = true
  }
  finally {
    loading.value = false
  }
}

onMounted(reload)

const fmt = (sec: number): string =>
  sec ? new Date(sec * 1000).toLocaleDateString('ja-JP') : '-'

const scopeLabel = (scope: string): string =>
  scope
    .split(/\s+/)
    .filter(Boolean)
    .map(s => (s === 'user-mcp/read' ? '読む' : s === 'user-mcp/write' ? '書く' : s))
    .join(' / ')

const onConfirmRevoke = async (): Promise<void> => {
  if (!confirmJti.value) return
  revoking.value = true
  try {
    await revokeToken(confirmJti.value)
    await reload()
  }
  catch {
    error.value = true
  }
  finally {
    revoking.value = false
    confirmJti.value = null
  }
}
</script>

<template>
  <v-card
    class="mcp-tokens"
    variant="outlined"
  >
    <v-card-title class="d-flex align-center">
      <v-icon
        :icon="mdiRobotOutline"
        class="mr-2"
      />
      外部エージェント接続（MCP）
    </v-card-title>
    <v-card-text>
      <p class="mcp-desc">
        Claude Code 等の外部エージェントから、あなたの「羽根筆のゆりかご」の白い魔導書を
        読み書きするための接続トークンです。心当たりのない接続は失効してください。
      </p>

      <v-alert
        v-if="!configured"
        type="info"
        variant="tonal"
        density="compact"
      >
        この環境では外部エージェント接続は未設定です。
      </v-alert>

      <template v-else>
        <v-progress-linear
          v-if="loading"
          indeterminate
        />
        <v-alert
          v-else-if="error"
          type="error"
          variant="tonal"
          density="compact"
        >
          接続情報の取得に失敗しました。時間をおいて再読み込みしてください。
        </v-alert>
        <p
          v-else-if="tokens.length === 0"
          class="mcp-empty"
        >
          まだ接続はありません。
        </p>

        <v-list
          v-else
          density="compact"
          class="mcp-list"
        >
          <v-list-item
            v-for="t in tokens"
            :key="t.jti"
            :title="scopeLabel(t.scope)"
            :subtitle="`発行: ${fmt(t.createdAt)} / 有効期限: ${fmt(t.expiresAt)}`"
          >
            <template #append>
              <v-chip
                v-if="t.revoked"
                color="grey"
                size="small"
                variant="tonal"
              >
                失効済み
              </v-chip>
              <v-btn
                v-else
                :prepend-icon="mdiDelete"
                color="error"
                variant="text"
                size="small"
                @click="confirmJti = t.jti"
              >
                失効する
              </v-btn>
            </template>
          </v-list-item>
        </v-list>
      </template>
    </v-card-text>

    <v-dialog
      :model-value="!!confirmJti"
      max-width="420"
      @update:model-value="confirmJti = null"
    >
      <v-card>
        <v-card-title>接続を失効しますか？</v-card-title>
        <v-card-text>
          この接続トークンは無効になり、該当エージェントからは操作できなくなります。
          再び使うには接続をやり直してください。
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            variant="text"
            :disabled="revoking"
            @click="confirmJti = null"
          >
            やめる
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="revoking"
            @click="onConfirmRevoke"
          >
            失効する
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<style scoped>
.mcp-tokens {
  font-family: "Noto Sans JP", sans-serif;
}

.mcp-desc {
  font-size: 0.85rem;
  line-height: 1.6;
  opacity: 0.8;
  margin-bottom: 12px;
}

.mcp-empty {
  font-size: 0.9rem;
  opacity: 0.7;
  padding: 8px 0;
}
</style>
